import { createSlice } from "@reduxjs/toolkit";
import { about_html } from "../Data/About";
import { faqs_html } from "../Data/Faqs";
import { guidelines_html } from "../Data/Guidelines";
import { help_document_html } from "../Data/DocumentationWeb";
import { help_videos_html } from "../Data/VideosWeb";
import { tickets_html } from "../Data/TicketsWeb";
import { announce_html } from "../Data/Announcement";
import Common, { AUTHORIZATION_LEVEL } from "../Common/Common";

const initial_state = {
  places: [],
  markers: [],
  polylines: [],
  events: [],
  schedule: [],
  homeScreen: "",
  homeScreenKey: 0,
  selectedPlace: null,
  MapCenterPlace: null,
  SortBy: 0,
  SearchText: "",
  FilterVisited: false,
  FilterFavorites: false,
  DatesFilter: [],
  PlacesFilter: [],
  loc: { latitude: 0, longitude: 0 },
  GroupsFilter: [],
  CategoriesFilter: [],
  Section: null,
  SearchResultsPlacesList: [],
  FilterSearched: "all",
  ShowHiddenPlaces: false,
  ScheduleName: "Main",
  ScheduleNames: [{ label: "Main", value: "Main" }],
  FilterNotVisited: false,
  FilterMustSee: false,
  FilterBucketList: false,
  SchedulesFilter: [],
  GroupsHide: [],
  DistanceFilter: -1,
  CitiesFilter: [],
  user_info_id: null,
  user_info: {
    distance_unit: "kilometers",
    date_format: "Month first",
    edit_mode: false,
    email: "guest@gmail.com",
    first_name: "guest",
    last_name: "user",
    uid: "guest",
    create_date: null,
    modify_date: null,
    travel_payouts_marker: "",
    last_guide_id: "",
    last_guide_time: "",
    payment_info: "",
    affiliate_program: false,
    default_duration: 30,
    event_time_rounding: 15,
  },
  creator_info: {
    distance_unit: "miles",
    date_format: "Month first",
    edit_mode: "Off",
    email: null,
    first_name: null,
    last_name: null,
    uid: null,
    create_date: null,
    travel_payouts_marker: "",
  },
  owner_info: {
    distance_unit: "miles",
    date_format: "Month first",
    edit_mode: "Off",
    email: null,
    first_name: null,
    last_name: null,
    uid: null,
    create_date: null,
    travel_payouts_marker: "",
  },
  AppSettings: {
    guide_info_id: "IQrZiedo7oJWa9GgLR6L",
    guide_info: null,
    help_document: help_document_html,
    help_videos: help_videos_html,
    about: about_html,
    tickets: tickets_html,
    faqs: faqs_html,
    guidelines: guidelines_html,
    announce: announce_html,
    overview_video: "https://youtu.be/46LDuyGnTrY",
    travel_payouts_marker: "292121",
    travel_payouts_marker_2: "292121",
    travel_payouts_marker_3: "292121",
    percent_1: 75,
    percent_2: 25,
    percent_3: 0,
  },
  showPlaceMenu: false,
  ShowHTMLReader: false,
  ShowMultiselect: false,
  MultiselectItems: [],
  MultiselectSelectedItems: [],
  MultiselectCallback: null,
  MultiselectMode: "multi",
  MultiselectSearchText: "",
  MultiselectTitle: "Select",
  SelectedEvent: null,
  HTMLToSHow: "",
  HTMLTitle: "HTML Content",
  LoadingGuide: false,
  EventSearchText: "",
  CurrentLocation: { latitude: 0, longitude: 0 },
  HTMLStack: [],
  MapCenter: {
    lat: 43.7713786,
    lng: 11.2543177,
  },
  MapBounds: {
    north: 43,
    south: 44,
    west: 7,
    east: 8,
  },
  MapBoundsValue: null,
  GuideLoaded: false,
  noFiltering: true,
  fromLoc: null,
  toLoc: null,
  waypointsArray: [],
  showHiddenPlace: [],
  MenuModalMeta: [],
  Authorization: AUTHORIZATION_LEVEL.NONE,
  GuideEditMode: true,
  guide_meta: { guide_info: null, guide_info_id: null, guide_id: null },
  GuideSortOption: "Modified date",
  MenuModalArgs: {
    Show: false,
    Image: null,
    List: [],
    Heading: "",
    Place: null,
  },
  GuideStack: [],
  MapFullScreen: false,
  HomeFullScreen: false,
  PlaceTypeFilter: [],
  CurrentAppScreen: "Home", // "Home", "Map", "Places", "Schedule", Guides
  // "Where did I come from" for a menu/link action that jumps straight to
  // another screen in mobile mode (PlaceModal/EventModal's "Center in
  // Map", Home.js's maplink/schedulelink) — CurrentAppScreen itself has no
  // history, it's just overwritten. A single value wasn't enough: chaining
  // several jumps (e.g. Itinerary -> schedulelink -> Schedule -> "Center
  // in Map" -> Map -> back -> Schedule) overwrote the first hop before it
  // was ever consumed, stranding the user on Schedule with no way back to
  // Itinerary. A real stack, but still scoped narrowly to these specific
  // screen-jump actions only — not a repeat of the broader parallel-
  // history mechanism tried and reverted for the link-based Itinerary
  // dialog itself (see memory: project_link_overlay_dialogs); this never
  // renders anything of its own, it only remembers which CurrentAppScreen
  // value to restore. Added 2026-09-09 as a single value, extended to a
  // stack the same day.
  AppScreenStack: [],
  // Set by EventModal.js's "Adjust Duration" flow when a duration edit was
  // saved with the recalculate toggle OFF and it introduced/left a real
  // overlap with the next event that day — an ISO date string (the day to
  // show the banner on) or null. SchedulePanel.js renders a persistent,
  // non-blocking "Recalculate this day?" banner while set, and clears it
  // once a recalculate actually runs (not just on dismiss — it should
  // reappear on any new conflict). Added 2026-09-10.
  DayConflictBanner: null,
  // Port of andiamo-places' identical flag (MainScreen.js) — deliberately
  // NOT general per-edit dirty tracking (nothing clears/sets it on every
  // small edit); only flipped true at a few known "batch change" completion
  // points (Build My Trip / Build Trip with AI / Import Itinerary from Image),
  // the same moments a user could lose real, hard-to-redo work by
  // navigating away or closing the tab without saving. Cleared on a
  // successful Save. MobileBottomNavBar.js's renderUnsavedGuideBanner reads
  // it (alongside guide_info_id == null for "never saved at all") to show a
  // "remember to save" nudge, matching mobile. Added 2026-09-30.
  GuideHasUnsavedChanges: false,
  // Drives the app-wide ConflictPreviewBanner (see purring-humming-mountain
  // plan) — true while the user is browsing the server's latest guide after
  // "View Latest Version" on a save conflict, until they choose Keep or
  // Revert. Global (not local component state) so the banner stays visible
  // across Map/Places/Schedule, not just whichever panel showed the
  // conflict dialog.
  PreviewingConflictSnapshot: false,
  // Lets any component (notably Home.js's "climatelink" scheme, opened from
  // an HTML note) open MobileBottomNavBar.js's Climate dialog without a
  // direct reference to that component's local state — {city, country, lat,
  // lon} (lat/lon set only for a specific place, letting the dialog skip
  // geocoding) or null when hidden. Added 2026-09-10.
  ClimateDialogParams: null,
  ShowWelcomeScreen: false, // gates the render tree for signed-out first-run visitors with no explicit ?id=
  GuidesOrPlaces: "Places", //"Places", "Guides"
  GuideType: "Featured", //"Featured", "Mine"
  MobileMode: (typeof window !== "undefined") && window.innerWidth < 981,
  StreetViewLocation: null,
  DirectionsFromLocation: null,
  DirectionsWaypointsLocation: [],
  DirectionsToLocation: null,
  HideSchedule: false,
  ScheduleDndEnabled: false,
  TodayMode: false,
  HidePlaces: false,
  HideMap: false,
  HideHome: false,
  HideSideMenu: true,
  MapRef: null,
  GuideSettings: {
    mode: "edit", // "edit" "view" "custom_view"
    zoom: 5,
    hide_schedule: false,
    hide_hidden: false,
    wrap_text: false,
    places_list: {
      menu: false,
      info: true,
      url: false,
      attributes: true,
      center: false,
      filter: false,
      path_link: false,
    },
    places_map: {
      menu: true,
      info: false,
      url: false,
      attributes: false,
      center: false,
      filter: false,
      path_link: false,
    },
    places_map_hover: {
      menu: false,
      place: true,
      info: true,
      event: true,
      path_link: false,
    },
    event: {
      menu: true,
      details: false,
      center: false,
      filter: false,
    },
    schedule: {
      menu: true,
      itinerary: false,
      center: false,
      filter: false,
    },
    day: {
      menu: true,
      center: false,
      filter: false,
    },
    itinerary: {
      directions: true,
      info: true,
      photo: false,
      place_menu: true,
      // Small/non-intrusive weather+climate links per event, added
      // 2026-09-10. On by default to match the per-action itinerary
      // checklist's own default, since that checklist now takes its
      // initial checked-state from this setting.
      weather: true,
      climate: true,
    },
  },
  ContentFontZoom: "120",
  GuideInfoArray: [],
  GuideInfoArrayMine: [],
  ViewedGuides: [],
  GuideSearchText: "",
  GuideCities: [],
  GuideCountries: [],
  GuideLanguages: [],
  GuideCitiesMine: [],
  GuideCountriesMine: [],
  GuideLanguagesMine: [],
  GuideCitiesSelected: [],
  GuideCountriesSelected: [],
  GuideLanguageSelected: "English",
  GuideCitiesMineSelected: [],
  GuideCountriesMineSelected: [],
  GuideLanguageMineSelected: "All",
  GuideOrderBy: "Modified date",
  GuideOrderAscending: true,
  HighlightedPlaces: [],
  ReplayState: "Stopped",
  ShowOffcanvas: false,
  PlayEventsIndex: 0,
  SizeMapToFit: true,
  NoAds: false,
  TempValue: null,
  TripAIShown: false,
  imageFolder: "",
  HomePageLayout: "Roam",
  // // GooglePlaces: new GooglePlaces(),
  // Attractions: new Attractions(),
  // Wikipedia: new Wikipedia(),
  // Flickr: new Flickr(),
  //HTMLContent: "",
  GuideFormat: "User Content",
};
// A guide with no guide_info_id has never been saved to the cloud — it's the
// in-memory guide the user just created (createLocalGuide), so it's treated
// like Guide.setAuthorization's guide_info == null case: full access. The
// update_guide_attributes branch of setGuide re-syncs guide_meta.guide_info
// from window.guide_obj.attributes, whose user_id is "" and access [] until
// the first save — that used to downgrade the creator to NONE, so Save said
// "Not authorized to save this guide" (e.g. right after Build Trip with AI
// built into "A new guide"). Found 2026-09-30.
const guideInfoForAuth = (meta) => {
  if (!meta) return null;
  const neverSaved = meta.guide_info_id == null && (!meta.guide_info || !meta.guide_info.user_id);
  return neverSaved ? null : meta.guide_info;
};
// A place that can be shown on the map — places may have no location
// (photos without GPS, Wikipedia articles; 2026-10-17), and centring on one
// handed Google Maps NaN ("setCenter: not a LatLng").
export const isShape = (p) => !!p && (p.PType === "PolyLine" || p.PType === "Polygon") && typeof p.Points === "string" && p.Points.includes(",");
const hasMapLocation = (p) => {
  if (!p) return false;
  const lat = parseFloat(p.Lat), lng = parseFloat(p.Longi);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};


const slice1 = createSlice({
  name: "sections",
  initialState: initial_state,
  // InitialPlaces, //

  reducers: {
    setGuide: (state, action) => {
      console.log("##setGuide: action.payload", action.payload.action);
      if (action.payload.action === "clear"){
        console.log("##setGuide: action.payload.action", action.payload.action)
        if (window.guide_obj == null) return state;
        state.places = [];
        state.schedule = [];
        state.events = [];
        state.markers = [];
        state.polylines = [];
        if(typeof window.guide_obj.places !== "undefined")
          window.guide_obj.places.placesList = [];
        window.guide_obj.schedule.events = [];
        window.guide_obj.groups = window.guide_obj.groups.filter(g => parseInt(g.id) <= 999);
        window.guide_obj.categories = window.guide_obj.categories.filter(c => parseInt(c.id) <= 999);
        {
          const _isGuest = state.user_info && state.user_info.uid === "guest";
          const it = state.GuideSettings.itinerary;
          const _isMosaicIt = it && it.directions === false && it.info === false && it.photo === true && it.place_menu === false;
          const clearedMessage = "Nothing here yet. Add destinations to build your trip, or tap Edit to write this page yourself."; // plain wording (2026-10-23)
          window.guide_obj.document = clearedMessage;
          state.homeScreen = (it && _isMosaicIt && !_isGuest)
            ? window.guide_obj.createHTMLItinerary("Main", it, { ...state.user_info, showHiddenPlaces: false }, state.GuideSettings)
            : clearedMessage;
        }
        state.homeScreenKey += 1;
        window.guide_obj.attributes.countries = [];
        window.guide_obj.attributes.cities = [];
        window.guide_obj.attributes.guide_name = "New Guide";
        window.guide_obj.attributes.description = "New Guide";
        // The picture goes too (2026-10-23, matches mobile): a blank guide
        // kept the previous guide's photo (on a first visit, the sample
        // guide's), so in the Library it looked like that guide.
        window.guide_obj.attributes.image = "";
        window.guide_obj.attributes.image_m = "";
        // destinations/aiPreferences are guide CONTENT, same category as
        // places/schedule/groups/categories above — left unreset here, a
        // guide's destinations survived "Start a new guide" (WelcomeScreen's
        // "blank" action -> _clearGuide -> this "clear" dispatch) from
        // whatever guide was open before (typically the sample guide shown
        // to a first-time/guest visitor). TripAI/BuildMyTripWizard seed
        // their own step/destinations state directly from
        // window.guide_obj.destinations, so a "new" guide with a leftover
        // destination opened straight to "step 2 of 3" instead of a real
        // step 1 — confusing since nothing had actually been added yet.
        // Found 2026-09-07.
        window.guide_obj.destinations = [];
        window.guide_obj.aiPreferences = null;
        window.guide_obj.photoBookLayout = null;
        // Same category as destinations/aiPreferences above — flight status
        // is guide CONTENT, not a setting, and was left surviving a "Clear
        // Guide Content"/"Replace this guide" the same way destinations did
        // before the 2026-09-07 fix above. Found 2026-09-14.
        window.guide_obj.flights = [];
        // access/image/image_m must stay as they are — same reasoning as
        // guide_info_id/guide_id below: every current caller of this
        // "clear" action (the standalone "Clear Guide" menu item, and
        // Build My Trip/TripAI's "Replace this guide") clears CONTENT only
        // and expects Save afterward to update the SAME existing cloud
        // guide, sharing permissions and cover image included. Resetting
        // access here silently stripped a guide's sharing (e.g. "shared to
        // all to copy") the moment someone ran "Replace this guide" on it —
        // found 2026-09-02, same root cause as the guide_id bug just above.
        // A genuinely NEW, never-saved guide already gets fresh
        // access:[]/image:""/image_m:"" from GuideAttributes' own
        // constructor and never goes through this "clear" action at all, so
        // nothing here needs to force them.
        // This clears CONTENT, not settings — schedule_day_start/end
        // previously stayed at whatever the guide had before (e.g. a
        // guest's shared sample guide, which has a real, already-saved
        // value from before this was 9/20 by default), so "Start a new
        // guide" silently inherited stale hours instead of the current app
        // default. Reset them the same way the settings-defaults
        // normalization above would for a guide with no value at all.
        // Write to window.guide_obj.guide_settings directly rather than
        // assuming it's still the same object as state.GuideSettings: Immer
        // (RTK's producer under the hood) decouples them the first time ANY
        // reducer mutates a field on state.GuideSettings after the earlier
        // `state.GuideSettings = window.guide_obj.guide_settings` aliasing
        // assignment — that mutation clones a fresh object for the Redux
        // state side, leaving window.guide_obj.guide_settings (the object
        // Firestore actually saves, and what recalculateSchedule/Day read)
        // pointing at the old, stale value. Setting both here, rather than
        // relying on the alias still holding, is what actually fixes it.
        window.guide_obj.guide_settings.schedule_day_start = 9;
        window.guide_obj.guide_settings.schedule_day_end = 20;
        state.GuideSettings = window.guide_obj.guide_settings;
        state.guide_meta.guide_info = window.guide_obj.attributes;
        // guide_info_id/guide_id must stay pointing at the guide that was
        // open before this clear — every current caller of this "clear"
        // action (the standalone "Clear Guide" menu item, and Build My
        // Trip/TripAI's "Replace this guide") clears CONTENT only and
        // expects Save afterward to overwrite that same existing cloud
        // guide, exactly as "Clear Guide"'s own confirm text promises
        // ("changes will be local until you save the guide... refresh the
        // guide to read the content last saved to the cloud"). Nulling
        // these here broke both of those — Save silently created a brand
        // new guide instead of updating the one just replaced (found
        // 2026-09-01 via "Build My Trip" -> "Replace this guide" -> Save).
        // A genuinely NEW, never-saved guide already resets its own
        // guide_meta independently (see MobileBottomNavBar.js's
        // createLocalGuide) and never goes through this "clear" action at
        // all, so nothing here needs to special-case that scenario.

        state.FilterVisited = false;
        state.FilterNotVisited = false;
        state.FilterFavorites = false;
        state.ShowHiddenPlaces = false;
        state.FilterMustSee = false;
        state.FilterBucketList = false;
        state.SchedulesFilter = [];
        state.DatesFilter = [];
        state.PlacesFilter = [];
        state.GroupsFilter = [];
        state.CategoriesFilter = [];
        state.FilterSearched = "all";
        state.GroupsHide = [];
        state.SearchText = "";
        state.DistanceFilter = -1;
        state.CitiesFilter = [];
        state.showHiddenPlace = [];
        state.PlaceTypeFilter = [];
        state.noFiltering = true;
        state.HTMLStack = [];
        state.ScheduleName = "Main";
        state.ScheduleNames = [{ label: "Main", value: "Main" }];
        
        state.MapBounds =  {
          north: 43,
          south: 44,
          west: 7,
          east: 8,
        };
        return state;
      }

      if (window.guide_obj == null) return state;

      if (action.payload.guide != null) {
        state.FilterVisited = false;
        state.FilterNotVisited = false;
        state.FilterFavorites = false;
        state.ShowHiddenPlaces = false;
        state.FilterMustSee = false;
        state.FilterBucketList = false;
        state.SchedulesFilter = [];
        state.DatesFilter = [];
        state.PlacesFilter = [];
        state.GroupsFilter = [];
        state.CategoriesFilter = [];
        state.FilterSearched = "all";
        state.GroupsHide = [];
        state.SearchText = "";
        state.DistanceFilter = -1;
        state.showHiddenPlace = [];
        state.SearchResultsPlacesList = [];
        state.PlaceTypeFilter = [];
        state.CitiesFilter = [];
        // A fresh guide load only — never on a plain refresh/refilter
        // (guide: null, dispatched constantly elsewhere) — so this can
        // never clobber a user's active "Set as Current Location" choice
        // made later in the same session; that action writes to this same
        // CurrentLocation field directly (PlaceModal.js/PlaceMenu.js) and
        // isn't persisted to the guide, so it only needs protecting from
        // THIS reducer re-seeding it, not from anything else.
        //
        // Before this, web's CurrentLocation stayed at its {0,0} initial
        // value until real GPS resolved, which tripped a DIFFERENT fallback
        // in Guide.js's distance calculations that substitutes the FIRST
        // place in the currently sorted/filtered list — an unstable anchor
        // that shifts whenever sort/filter state changes, and a
        // meaningless one for a multi-destination guide (Rome + Florence
        // showing every Florence place's "distance" relative to an
        // arbitrary Roman café). base_location is a stable, guide-level
        // attribute representing the destination as a whole — mobile
        // already seeds CurrentLocation from it on every fresh guide load
        // (Reducers.js's DOWNLOAD_GUIDE_FB case); this brings web in line.
        // Found 2026-09-02 via a user noticing different distances for the
        // same guide on web vs. mobile.
        if (action.payload.guide === "new") {
          state.CurrentLocation = {
            latitude: window.guide_obj.attributes.base_location?.latitude ?? 0,
            longitude: window.guide_obj.attributes.base_location?.longitude ?? 0,
          };
        }
      }

      const res = window.guide_obj.preFilterPlaces(
        state.SearchText,
        state.FilterVisited,
        state.FilterFavorites,
        state.DatesFilter,
        state.PlacesFilter,
        state.CurrentLocation,
        state.GroupsFilter,
        state.CategoriesFilter,
        state.Section,
        state.SearchResultsPlacesList,
        state.FilterSearched,
        state.ShowHiddenPlaces,
        state.ScheduleName,
        state.FilterNotVisited,
        state.FilterMustSee,
        state.SchedulesFilter,
        state.GroupsHide,
        state.DistanceFilter,
        state.user_info,
        state.showHiddenPlace,
        state.PlaceTypeFilter,
        state.CitiesFilter,
        state.FilterBucketList
      );

      //console.log("res.filteredPlacesList", res.filteredPlacesList);

      let _places = window.guide_obj.sortPlaces(
        res.filteredPlacesList,
        state.SortBy,
        state.user_info,
        state.CurrentLocation
      );
      // _places.forEach((section) => {
      //   for (let p = 0; p < section.data.length; p++) section.data[p].place = section.data[p].place.ID;
      // });

      //console.log("_places", _places);
      state.places = _places;
      state.GuideSettings = window.guide_obj.guide_settings;
      if (typeof state.GuideSettings.places_map_hover === "undefined") state.GuideSettings.places_map_hover = {};
      if (typeof state.GuideSettings.places_map_hover.menu === "undefined")
        state.GuideSettings.places_map_hover.menu = false;
      if (typeof state.GuideSettings.places_map_hover.place === "undefined")
        state.GuideSettings.places_map_hover.place = false;
      if (typeof state.GuideSettings.places_map_hover.info === "undefined")
        state.GuideSettings.places_map_hover.info = false;
      if (typeof state.GuideSettings.places_map_hover.event === "undefined")
        state.GuideSettings.places_map_hover.event = false;
      if (typeof state.GuideSettings.show_photo_icons === "undefined")
        state.GuideSettings.show_photo_icons = false;
      // click_behavior decouples "which click behavior" (places_list/
      // places_map/event/schedule/day toggles vs. the default full menu)
      // from `mode` (home-page rendering format: Mosaic vs Custom), which
      // conflated the two since this app's original design. A guide saved
      // before this field existed is migrated to whichever value preserves
      // its CURRENT visible behavior exactly — mode drove click behavior
      // until now, so derive from it once, here, not on every load. Added
      // 2026-10-05.
      if (typeof state.GuideSettings.click_behavior === "undefined") {
        state.GuideSettings.click_behavior = state.GuideSettings.mode === "custom_view" ? "custom" : "default";
      }
      // if (typeof state.GuideSettings.places_map_hover.path_link === "undefined")
      //   state.GuideSettings.places_map_hover.path_link = false;
      if (typeof state.GuideSettings.places_map.path_link === "undefined")
        state.GuideSettings.places_map.path_link = false;

      if (typeof state.GuideSettings.schedule_day_start === "undefined") state.GuideSettings.schedule_day_start = 9;
      if (typeof state.GuideSettings.schedule_day_end === "undefined") state.GuideSettings.schedule_day_end = 20;
      if (typeof state.GuideSettings.mosaic_schedules === "undefined") state.GuideSettings.mosaic_schedules = true;
      if (typeof state.GuideSettings.mosaic_groups === "undefined") state.GuideSettings.mosaic_groups = true;
      if (typeof state.GuideSettings.mosaic_all === "undefined") state.GuideSettings.mosaic_all = true;

      if (typeof state.GuideSettings.itinerary === "undefined") state.GuideSettings.itinerary = {};
      if (typeof state.GuideSettings.itinerary.info === "undefined") state.GuideSettings.itinerary.info = false;
      if (typeof state.GuideSettings.itinerary.photo === "undefined") state.GuideSettings.itinerary.photo = false;
      if (typeof state.GuideSettings.itinerary.place_menu === "undefined")
        state.GuideSettings.itinerary.place_menu = true;

      // mode is authoritative — reconcile itinerary flags for guides saved before itinerary was kept in sync
      if (state.GuideSettings.mode === "edit") {
        state.GuideSettings.itinerary.directions = false;
        state.GuideSettings.itinerary.info = false;
        state.GuideSettings.itinerary.photo = true;
        state.GuideSettings.itinerary.place_menu = false;
      } else if (state.GuideSettings.mode === "custom_view") {
        state.GuideSettings.itinerary.directions = true;
        state.GuideSettings.itinerary.info = true;
        state.GuideSettings.itinerary.photo = true;
        state.GuideSettings.itinerary.place_menu = true;
      }

      const _isMosaicSettings = state.GuideSettings.itinerary.directions === false && state.GuideSettings.itinerary.info === false && state.GuideSettings.itinerary.photo === true && state.GuideSettings.itinerary.place_menu === false;
      state.GuideFormat = _isMosaicSettings ? "Photo Mosaic" : "User Content";

      if (action.payload.guide != null) {
        const _isGuest = state.user_info && state.user_info.uid === "guest";
        const it = state.GuideSettings.itinerary;
        const _isMosaicIt = it && it.directions === false && it.info === false && it.photo === true && it.place_menu === false;

        // Preserve the original mosaic itinerary for toggling back to it later
        if (_isMosaicIt) window.guide_obj._originalItinerary = JSON.parse(JSON.stringify(it));

        // Guests default to User Content: override itinerary so GuideFormat = "User Content"
        // and all subsequent setGuide(null) recalculations stay consistent
        if (_isGuest && _isMosaicIt) {
          state.GuideSettings.itinerary = { ...it, directions: true, info: true, place_menu: true };
          window.guide_obj.guide_settings.itinerary = { ...state.GuideSettings.itinerary };
          state.GuideFormat = "User Content";
        }

        let html = window.guide_obj.document;
        if (html === null || html === "" || html === "<!-- Comment -->") {
          html = window.guide_obj.createGuideHTML();
        }
        if (it && _isMosaicIt && !_isGuest)
          html = window.guide_obj.createHTMLItinerary("Main", it, { ...state.user_info, showHiddenPlaces: state.ShowHiddenPlaces }, state.GuideSettings);
        let htmlString = html;
        state.HTMLStack = [];
        state.HTMLStack.push(htmlString);
        state.homeScreen = htmlString;
        state.homeScreenKey += 1;
        if (action.payload.guide_meta != null && typeof action.payload.guide_meta !== "undefined")
          state.guide_meta = action.payload.guide_meta;
        const _Authorization = window.guide_obj.setAuthorization(
          guideInfoForAuth(action.payload.guide_meta != null ? action.payload.guide_meta : state.guide_meta),
          state.user_info.uid
        );
        state.Authorization = _Authorization;
      } else if (action.payload.guide_meta != null) {
        state.guide_meta = {
          guide_info: window.guide_obj.attributes,
          guide_info_id: state.guide_meta.guide_info_id,
          guide_id: state.guide_meta.guide_id,
        };
      }

      const _MarkersToDisplay = res.filteredPlacesList;
      //const _region = res.MapBounds;
      //const _MapRegion = window.guide_obj.places.getMapRegion(_MarkersToDisplay, null);
      const _polylines = res.polylines;
      _polylines.forEach((polyline) => {
        polyline.path = polyline.coordinates.map((ll) => ({ lat: ll.latitude, lng: ll.longitude }));
      });

      state.markers = _MarkersToDisplay;
      state.polylines = _polylines;
      // if (_MarkersToDisplay.length !== 0) {
      let maxMins = window.guide_obj.places.getMaxMinLatLon(res.filteredPlacesList);
      //const max_mins = { max_lat: max_lat, max_lon: max_lon, min_lat: min_lat, min_lon: min_lon };
      let MapBounds = {
        north: maxMins.max_lat,
        south: maxMins.min_lat,
        west: maxMins.min_lon,
        east: maxMins.max_lon,
      };
      //console.log("################### MapBounds", state.MapBounds);
      state.MapCenter = {
        lat: (maxMins.max_lat + maxMins.min_lat) / 2,
        lng: (maxMins.max_lon + maxMins.min_lon) / 2,
      };
      if (typeof window.guide_obj.attributes.base_location === "undefined")
        window.guide_obj.attributes.base_location = { latitude: 0, longitude: 0 };
      if (_MarkersToDisplay.length === 0 && _polylines.length === 0) {
        state.MapCenter = {
          lat: window.guide_obj.attributes.base_location.latitude,
          lng: window.guide_obj.attributes.base_location.longitude,
        };
        MapBounds = {
          north: window.guide_obj.attributes.base_location.latitude + 5,
          south: window.guide_obj.attributes.base_location.latitude - 5,
          west: window.guide_obj.attributes.base_location.longitude - 5,
          east: window.guide_obj.attributes.base_location.longitude + 5,
        };
      }
      //if (action.payload.fitToBounds === true)
      state.MapBounds = MapBounds;
      if (state.GuidesOrPlaces === "Guides") {
        if (state.GuideType === "Featured") maxMins = Common.getMaxMinLatLon(state.GuideInfoArray);
        else maxMins = Common.getMaxMinLatLon(state.GuideInfoArrayMine);
        MapBounds = {
          north: maxMins.max_lat,
          south: maxMins.min_lat,
          west: maxMins.min_lon,
          east: maxMins.max_lon,
        };
        state.MapCenter = {
          lat: (maxMins.max_lat + maxMins.min_lat) / 2,
          lng: (maxMins.max_lon + maxMins.min_lon) / 2,
        };
      }
      if (action.payload.update_guide_attributes === true) {
        let html = window.guide_obj.document;
        if (html === null || html === "" || html === "<!-- Comment -->") {
          html = window.guide_obj.createGuideHTML();
        }
        {
          const _isGuest = state.user_info && state.user_info.uid === "guest";
          const it = state.GuideSettings.itinerary;
          const _isMosaicIt = it && it.directions === false && it.info === false && it.photo === true && it.place_menu === false;
          if (it && _isMosaicIt && !_isGuest)
            html = window.guide_obj.createHTMLItinerary("Main", it, { ...state.user_info, showHiddenPlaces: state.ShowHiddenPlaces }, state.GuideSettings);
        }
        //console.log(html);
        let htmlString = html;
        //let htmlString = urlSchemeToFunctions(html);
        state.HTMLStack = [];
        state.HTMLStack.push(htmlString);
        state.homeScreen = htmlString;
        state.homeScreenKey += 1;

        state.guide_meta = {
          guide_info: window.guide_obj.attributes,
          guide_info_id: state.guide_meta.guide_info_id,
          guide_id: state.guide_meta.guide_id,
        };
      }
      if (
        (action.payload.guide != null || action.payload.fitToBounds === true) &&
        (state.SizeMapToFit || action.payload.override === true)
      )
        if (window.mapRef !== null && typeof window.mapRef !== "undefined")
          window.mapRef.fitBounds(MapBounds);

      //console.log("***************Center", state.MapCenter);
      // }
      state.schedule = window.guide_obj.schedule.getScheduleItems(
        state.EventSearchText,
        state.ScheduleName,
        window.guide_obj.places
      );
      if (state.schedule.length === 0 && state.ScheduleName !== "Main") {
        state.ScheduleName = "Main";
        state.schedule = window.guide_obj.schedule.getScheduleItems(
          state.EventSearchText,
          state.ScheduleName,
          window.guide_obj.places
        );
      }

      state.ScheduleNames = window.guide_obj.schedule.geScheduleNames();

      state.GuideLoaded = true;
      state.MapCenterPlace = null;

      state.noFiltering =
        state.FilterFavorites === false &&
        state.FilterSearched === "all" &&
        state.FilterMustSee === false &&
        state.FilterBucketList === false &&
        state.ShowHiddenPlaces === false &&
        state.FilterVisited === false &&
        state.FilterNotVisited === false &&
        state.SchedulesFilter.length === 0 &&
        state.DatesFilter.length === 0 &&
        state.PlacesFilter.length === 0 &&
        state.GroupsFilter.length === 0 &&
        state.GroupsHide.length === 0 &&
        state.CategoriesFilter.length === 0 &&
        state.DistanceFilter === -1 &&
        state.PlaceTypeFilter.length === 0 &&
        state.CitiesFilter.length === 0;

      const _Authorization = window.guide_obj.setAuthorization(guideInfoForAuth(state.guide_meta), state.user_info.uid);
      state.Authorization = _Authorization;
      state.GuideEditMode =
        // state.GuideSettings.mode === "edit" &&
        state.user_info.edit_mode !== "Off";
      return state;
    },
    // setBaseLocation : (state: action) {
    //   window.guide_obj.attributes.base_location = action.payload.value;
    //   state.MenuModalArgs = action.payload.va

    //   this.attributes.base_location = {
    //     latitude: parseFloat(this.places.placesList[0].Lat),
    //     longitude: parseFloat(this.places.placesList[0].Longi),
    //   };
    // }
    setGuideSettings: (state, action) => {
      state.GuideSettings = action.payload.value;
      const _itin = state.GuideSettings.itinerary;
      const _isMosaic = _itin && _itin.directions === false && _itin.info === false && _itin.photo === true && _itin.place_menu === false;
      state.GuideFormat = _isMosaic ? "Photo Mosaic" : "User Content";
      window.guide_obj.guide_settings = action.payload.value;
      state.GuideEditMode =
        // state.GuideSettings.mode === "edit" &&
        state.user_info.edit_mode !== "Off";
      // Regenerate the currently-displayed Main page when it's the Photo
      // Mosaic view — otherwise this reducer only ever updated the settings
      // object itself, never the already-rendered HTML sitting in
      // state.homeScreen, so toggling "Mosaic groups"/"Mosaic schedules" (or
      // any other itinerary-affecting setting) saved correctly but had no
      // visible effect until something else happened to regenerate the
      // page. Matches setGuide's identical regeneration pattern above.
      // Found 2026-09-27.
      if (_isMosaic) {
        const html = window.guide_obj.createHTMLItinerary("Main", _itin, { ...state.user_info, showHiddenPlaces: state.ShowHiddenPlaces }, state.GuideSettings);
        state.HTMLStack = [html];
        state.homeScreen = html;
        state.homeScreenKey += 1;
      }
      return state;
    },
    setImageFolder: (state, action) => {
      state.imageFolder = action.payload.value;
      return state;
    },
    setNoAds: (state, action) => {
      state.NoAds = action.payload.value;
      return state;
    },
    setTripAIShown: (state, action) => {
      state.TripAIShown = action.payload.value;
      return state;
    },
    
    setTempValue: (state, action) => {
      state.TempValue = action.payload.value;
      return state;
    },
    setHideSchedule: (state, action) => {
      state.HideSchedule = action.payload.value;
      return state;
    },
    setScheduleDndEnabled: (state, action) => {
      state.ScheduleDndEnabled = action.payload.value;
      return state;
    },
    setTodayMode: (state, action) => {
      state.TodayMode = action.payload.value;
      return state;
    },
    setShowOffcanvas: (state, action) => {
      state.ShowOffcanvas = action.payload.value;
      return state;
    },
    setHidePlaces: (state, action) => {
      state.HidePlaces = action.payload.value;
      return state;
    },
    setHideMap: (state, action) => {
      state.HideMap = action.payload.value;
      if (state.HideHome === true) state.HideHome = false;
      return state;
    },
    setHideHome: (state, action) => {
      state.HideHome = action.payload.value;
      if (state.HideMap === true) state.HideMap = false;
      return state;
    },
    setHideSideMenu: (state, action) => {
      state.HideSideMenu = action.payload.value;
      return state;
    },
    setHighlightedPlaces: (state, action) => {
      state.HighlightedPlaces = action.payload.value;
      return state;
    },
    // Trip replay (2026-09-05, ported from andiamo-places): the play/pause/
    // stop buttons live in PlacesHeader.js (the mobile-mode top bar shown
    // over the Map screen), but the actual replay engine (timer,
    // window.mapRef.panTo, the tooltip overlay) lives in Map.js — a
    // completely separate component with no direct prop/ref path to
    // PlacesHeader.js. Redux is the existing cross-component pattern this
    // app already uses for exactly this kind of thing (see
    // centerMapToPlaces/HighlightedPlaces above), so this follows it rather
    // than inventing a new communication path.
    setReplayState: (state, action) => {
      state.ReplayState = action.payload.value;
      return state;
    },
    setGuideOrderBy: (state, action) => {
      state.GuideOrderBy = action.payload.value;
      return state;
    },
    setGuideOrderAscending: (state, action) => {
      state.GuideOrderAscending = action.payload.value;
      return state;
    },
    setGuideCities: (state, action) => {
      state.GuideCities = action.payload.value;
      return state;
    },
    setGuideCountries: (state, action) => {
      state.GuideCountries = action.payload.value;
      return state;
    },
    setGuideLanguages: (state, action) => {
      state.GuideLanguages = action.payload.value;
      return state;
    },
    setGuideCitiesSelected: (state, action) => {
      state.GuideCitiesSelected = action.payload.value;
      return state;
    },
    setGuideCountriesSelected: (state, action) => {
      state.GuideCountriesSelected = action.payload.value;
      return state;
    },
    setGuideLanguageSelected: (state, action) => {
      state.GuideLanguageSelected = action.payload.value;
      return state;
    },
    setGuideInfoArray: (state, action) => {
      state.GuideInfoArray = action.payload.value;
      return state;
    },

    setGuideInfoArrayMine: (state, action) => {
      state.GuideInfoArrayMine = action.payload.value;
      return state;
    },
    setGuideCitiesMine: (state, action) => {
      state.GuideCitiesMine = action.payload.value;
      return state;
    },

    setGuideCountriesMine: (state, action) => {
      state.GuideCountriesMine = action.payload.value;
      return state;
    },
    setGuideLanguagesMine: (state, action) => {
      state.GuideLanguagesMine = action.payload.value;
      return state;
    },
    setGuideCitiesMineSelected: (state, action) => {
      state.GuideCitiesMineSelected = action.payload.value;
      return state;
    },
    setPlayEventsIndex: (state, action) => {
      state.PlayEventsIndex = action.payload.value;
      return state;
    },
    setGuideCountriesMineSelected: (state, action) => {
      state.GuideCountriesMineSelected = action.payload.value;
      return state;
    },
    setGuideLanguagesMineSelected: (state, action) => {
      state.GuideLanguagesMineSelected = action.payload.value;
      return state;
    },
    setGuideCitiesMineSeSelected: (state, action) => {
      state.GuideCitiesMineSeSelected = action.payload.value;
      return state;
    },
    setGuideCountriesMineSeSelected: (state, action) => {
      state.GuideCountriesMineSeSelected = action.payload.value;
      return state;
    },
    setGuideLanguageMineSelected: (state, action) => {
      state.GuideLanguageMineSelected = action.payload.value;
      return state;
    },
    setSizeMapToFit: (state, action) => {
      state.SizeMapToFit = action.payload.value;
      return state;
    },
    setContentFontZoom: (state, action) => {
      state.ContentFontZoom = action.payload.value;
      return state;
    },
    setMapRef: (state, action) => {
      state.MapRef = action.payload.value;
      return state;
    },
    setDirectionsFromLocation: (state, action) => {
      state.DirectionsFromLocation = action.payload.value;
      return state;
    },
    setDirectionsWaypointsLocation: (state, action) => {
      state.DirectionsWaypointsLocation = action.payload.value;
      return state;
    },
    setDirectionsToLocation: (state, action) => {
      state.DirectionsToLocation = action.payload.value;
      return state;
    },
    setMobileMode: (state, action) => {
      state.MobileMode = action.payload.value;
      return state;
    },
    setPlaceTypeFilter: (state, action) => {
      state.PlaceTypeFilter = action.payload.value;
      return state;
    },
    setCurrentAppScreen: (state, action) => {
      state.CurrentAppScreen = action.payload.value;
      return state;
    },
    pushAppScreen: (state, action) => {
      state.AppScreenStack.push(action.payload.value);
      return state;
    },
    popAppScreen: (state) => {
      state.AppScreenStack.pop();
      return state;
    },
    clearAppScreenStack: (state) => {
      state.AppScreenStack = [];
      return state;
    },
    setDayConflictBanner: (state, action) => {
      state.DayConflictBanner = action.payload.value;
      return state;
    },
    setGuideHasUnsavedChanges: (state, action) => {
      state.GuideHasUnsavedChanges = action.payload.value;
      return state;
    },
    setPreviewingConflictSnapshot: (state, action) => {
      state.PreviewingConflictSnapshot = action.payload.value;
      return state;
    },
    setClimateDialogParams: (state, action) => {
      state.ClimateDialogParams = action.payload.value;
      return state;
    },
    setShowWelcomeScreen: (state, action) => {
      state.ShowWelcomeScreen = action.payload.value;
      return state;
    },
    setGuidesOrPlaces: (state, action) => {
      state.GuidesOrPlaces = action.payload.value;
      state.GuideType = action.payload.GuideType;
      return state;
    },
    setMenuModalArgs: (state, action) => {
      state.MenuModalArgs = action.payload.value;
      return state;
    },
    setMapFullScreen: (state, action) => {
      state.MapFullScreen = action.payload.value;
      return state;
    },
    setHomeFullScreen: (state, action) => {
      state.HomeFullScreen = action.payload.value;
      return state;
    },
    setUserInfoId: (state, action) => {
      state.user_info_id = action.payload.value;
      return state;
    },
    setSearchResultsPlacesList: (state, action) => {
      let new_value = [];
      action.payload.value.forEach((v) => {
        new_value.push(v);
      });
      // FilterSearched is deliberately left untouched here — matching
      // mobile's SET_OPENTRIPMAP_PLACES_BB, which never sets it either —
      // so search results appear mixed in with the guide's own places by
      // default (the mobile behavior), not auto-isolated into "only_search"
      // mode. Previously auto-switched to "only_search" on every search;
      // removed 2026-09-07 for cross-platform parity — see
      // memory: project_search_results_platform_parity (or ask the user
      // for the SearchResultsBanner's own manual "Only Search" toggle,
      // which still exists for anyone who wants that isolated view).
      state.SearchResultsPlacesList = new_value;
      return state;
    },
    addToSearchResultsPlacesList: (state, action) => {
      if (!action.payload.value) return state;
      let new_array = state.SearchResultsPlacesList;
      action.payload.value.forEach((v) => {
        if (!new_array.some((x) => x.Name === v.Name && x.Lat === v.Lat && x.Longi === v.Longi)) new_array.push(v);
      });

      state.SearchResultsPlacesList = new_array;
      return state;
    },
    addResultToGuide: (state, action) => {
      let _SearchResultsPlacesList = [...state.SearchResultsPlacesList];
      const index = _SearchResultsPlacesList.findIndex((x) => x.ID === action.payload.value);
      if (index === -1) return;
      window.guide_obj.places.addPlace(_SearchResultsPlacesList[index]);
      _SearchResultsPlacesList.splice(index, 1);
      state.SearchResultsPlacesList = _SearchResultsPlacesList;
      return state;
    },
    removeFromSearchResults: (state, action) => {
      let _SearchResultsPlacesList = [...state.SearchResultsPlacesList];
      const index = _SearchResultsPlacesList.findIndex((x) => x.ID === action.payload.value);
      if (index === -1) return;
      _SearchResultsPlacesList.splice(index, 1);
      state.SearchResultsPlacesList = _SearchResultsPlacesList;
      return state;
    },
    addAllResultsToGuide: (state, action) => {
      // "53" (Search) used to be stripped here on the assumption that a
      // place would get a real category assigned later once it joined the
      // guide — no such step exists (enrichment only ever handles
      // photo/info/hours), and mapGoogleTypesToCategoryId's "53" fallback is
      // now the legitimate final category value for an unmatched Google
      // place, resolved upstream in BuildMyTripWizard.js before this runs.
      // Stripping it here just silently discarded every unmatched place's
      // only category. Removed 2026-08-31.
      state.SearchResultsPlacesList.forEach((result) => {
        window.guide_obj.places.addPlace(result);
      });
      state.SearchResultsPlacesList = [];
      return state;
    },
    setMapBounds: (state, action) => {
      state.MapBounds = action.payload.value;
      return state;
    },
    setMapBoundsValue: (state, action) => {
      state.MapBoundsValue = action.payload.value;
      state.MapBounds = action.payload.value;
      console.log("setMapBoundsValue", action.payload.value);
      return state;
    },
    setStreetViewLocation: (state, action) => {
      state.StreetViewLocation = action.payload.value;
      return state;
    },
    getPlaceFromID: (state, action) => {
      state.place = window.guide_obj.places.getPlace(action.payload.guide_id);
      return state;
    },
    // setPlaces: (state, action) => {
    //   state.selectedPlace = action.payload.places;
    //   return state;
    // },
    setSelectedPlace: (state, action) => {
      state.selectedPlace = action.payload.place;
      return state;
    },
    setMapCenterPlace: (state, action) => {
      //console.log("setMapCenterPlace", action.payload.place);
      if (action.payload.place != null && !hasMapLocation(action.payload.place) && !isShape(action.payload.place)) {
        state.MapCenterPlace = null;
        return;
      }
      state.MapCenterPlace = action.payload.place;
      if (action.payload.place == null) return;
      const maxMins = window.guide_obj.places.getMaxMinLatLon(null, action.payload.place);
      let MapBounds = {
        north: maxMins.max_lat,
        south: maxMins.min_lat,
        west: maxMins.min_lon,
        east: maxMins.max_lon,
      };
      // const MapBounds = {
      //   north: action.payload.place.Lat + 0.001,
      //   south: action.payload.place.Lat - 0.001,
      //   west: action.payload.place.Longi - 0.001,
      //   east: action.payload.place.Longi + 0.001,
      // };
      if (window.mapRef !== null && typeof window.mapRef !== "undefined") window.mapRef.fitBounds(MapBounds);
    },
    centerMapToLoc: (state, action) => {
      // let MapBounds = {
      //   north: action.payload.value.Lat + 0.001,
      //   south: action.payload.value.Lat - 0.001,
      //   west: action.payload.value.Longi - 0.001,
      //   east: action.payload.value.Longi + 0.001,
      // };
      // console.log("centerMapToLoc", MapBounds);
      //if (state.MapRef !== null && typeof state.MapRef !== "undefined") state.MapRef.fitBounds(MapBounds);
      if (!hasMapLocation(action.payload.value)) return;
      const mapCenter = { lat: parseFloat(action.payload.value.Lat), lng: parseFloat(action.payload.value.Longi) };
      if (window.mapRef !== null && typeof window.mapRef !== "undefined") window.mapRef.panTo(mapCenter);
    },
    centerMapToPlaces: (state, action) => {
      // a path or area has no Lat/Longi of its own: it's shown by its points (2026-11-02, the crosshairs on a
      // walking route's card did nothing)
      const places = (action.payload.value || []).filter((p) => hasMapLocation(p) || isShape(p));
      if (window.mapRef === null || typeof window.mapRef === "undefined") return;
      if (places.length === 0) return;
      if (places.length === 1 && !isShape(places[0])) {
        window.mapRef.panTo({ lat: parseFloat(places[0].Lat), lng: parseFloat(places[0].Longi) });
        window.mapRef.setZoom(16);
        return;
      }
      const maxMins = window.guide_obj.places.getMaxMinLatLon(places, null);
      let MapBounds = {
        north: maxMins.max_lat + 0.0001,
        south: maxMins.min_lat - 0.0001,
        west: maxMins.min_lon - 0.0001,
        east: maxMins.max_lon + 0.0001,
      };
      window.mapRef.fitBounds(MapBounds);
    },
    setMapCenter: (state, action) => {
      state.MapCenter = action.payload.value;
      return state;
    },
    setHomeScreen: (state, action) => {
      //state.homeScreen = action.payload.value;
      let htmlString = action.payload.value;
      //let htmlString = urlSchemeToFunctions(action.payload.value);
      state.HTMLStack.push(htmlString);
      state.homeScreen = htmlString;
      state.homeScreenKey += 1;
      return state;
    },
    setCurrentLocation: (state, action) => {
      state.CurrentLocation = action.payload.value;
      return state;
    },
    seEvents: (state, action) => {
      state.events = action.payload.events;
      return state;
    },
    setMarkers: (state, action) => {
      state.markers = action.payload.markers;
      return state;
    },
    setShowPlaceMenu: (state, action) => {
      state.showPlaceMenu = action.payload.value;
      return state;
    },
    setHTMLToSHow: (state, action) => {
      console.log(state.GuideSettings.itinerary);
      let htmlString = action.payload.value;
      //let htmlString = urlSchemeToFunctions(action.payload.value);
      state.HTMLStack.push(htmlString);
      //state.HTMLToSHow = htmlString;
      state.homeScreen = htmlString;
      state.homeScreenKey += 1;
      return state;
    },
    resetHTMLScreen: (state, action) => {
      state.HTMLStack = [];
      state.HTMLStack.push(state.homeScreen);
    },
    setHTMLStack: (state, action) => {
      state.HTMLStack = action.payload.value;
      return state;
    },
    setHTMLTitle: (state, action) => {
      state.HTMLTitle = action.payload.value;
      return state;
    },
    popHTMLStack: (state) => {
      //console.log("popHTMLStack1", state.HTMLStack.length);
      window.getTime = function () {};
      let temp = state.HTMLStack;
      temp.pop();
      temp.pop();
      state.HTMLStack = temp;
      //console.log("popHTMLStack2", state.HTMLStack.length);
      return state;
    },
    clearHTMLStack: (state) => {
      state.HTMLStack = [];
      window.getTime = function () {};
      return state;
    },
    addViewedGuide: (state, action) => {
      const entry = action.payload; // { id, data }
      state.ViewedGuides = [
        entry,
        ...state.ViewedGuides.filter((g) => g.id !== entry.id),
      ].slice(0, 20);
      return state;
    },
    pushGuideStack: (state, action) => {
      window.getTime = function () {};
      let temp = state.GuideStack;
      temp.push(action.payload.value);
      state.GuideStack = temp;
      return state;
    },
    popGuideStack: (state) => {
      window.getTime = function () {};
      let temp = state.GuideStack;
      temp.pop();
      temp.pop();
      state.GuideStack = temp;
      return state;
    },
    resetHomeScreen: (state, action) => {
      window.getTime = function () {};
      let html = window.guide_obj.document;
      if (typeof html === "undefined" || html === null || html === "" || html === "<!-- Comment -->") {
        html = window.guide_obj.createGuideHTML();
      }
      {
        const _isGuest = state.user_info && state.user_info.uid === "guest";
        const it = state.GuideSettings.itinerary;
        const _isMosaicIt = it && it.directions === false && it.info === false && it.photo === true && it.place_menu === false;
        if (it && _isMosaicIt && !_isGuest) {
          // Was hardcoded to "Main" — so choosing a different schedule (e.g.
          // ScheduleHeader.js's "Choose Schedule") and then navigating to the
          // Home/Itinerary screen in Photo Mosaic format always showed
          // "Main"'s content regardless of the schedule actually selected.
          // Found 2026-09-27. action.payload.scheduleName is an explicit
          // override for callers that need the top-level Main mosaic grid
          // specifically, regardless of state.ScheduleName — e.g. App.js's
          // copySchedule sets ScheduleName to the newly copied schedule (for
          // Places/Map filtering) before this dispatches, and without the
          // override this rendered that schedule's own single-schedule
          // sub-page (with its own hero image) instead of the overview grid
          // the user actually expects to land on right after copying.
          const effectiveScheduleName = action.payload?.scheduleName || state.ScheduleName || "Main";
          html = window.guide_obj.createHTMLItinerary(effectiveScheduleName, it, { ...state.user_info, showHiddenPlaces: state.ShowHiddenPlaces }, state.GuideSettings);
          // createHTMLItinerary itself has no cover-image logic — every
          // caller has to prepend one (see HomeHeader.js's updateHomePage/
          // doItinerary, BuildMyTripWizard.js). This path never did, so
          // switching to a non-Main schedule always showed its content with
          // no guide cover image at all. Found 2026-09-27. Only for a
          // schedule-specific page (effectiveScheduleName !== "Main") — the
          // top-level Main grid is entirely composed of clickable tiles, and
          // a plain, non-clickable cover image there broke that consistency
          // (making it clickable would need the same iframe-to-parent
          // customLink bridge already found unreliable and removed for the
          // "editcoverlink" tile). A schedule-specific page behaves like a
          // single scrolling page instead, where a non-interactive hero
          // image is expected — matches doItinerary's existing convention.
          // Found 2026-09-27.
          if (effectiveScheduleName !== "Main") {
            const ILLUSTRATED_PLACEHOLDER = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg";
            const GOOGLE_PLACES_PHOTO_PATTERN = /places\.googleapis\.com\/v1\/.*\/media/i;
            const isBadCoverUrl = (url) => !url || url === ILLUSTRATED_PLACEHOLDER || GOOGLE_PLACES_PHOTO_PATTERN.test(url);
            const hasRealPhoto = (p) => (p.Photo && !isBadCoverUrl(p.Photo)) || (p.Photo_m && !isBadCoverUrl(p.Photo_m));
            const guideCustomCover = !isBadCoverUrl(window.guide_obj.attributes.image_m) ? window.guide_obj.attributes.image_m : null;
            const coverPlace = guideCustomCover ? null : (window.guide_obj.places.placesList || []).find(hasRealPhoto);
            const coverImageM = guideCustomCover || coverPlace?.Photo_m || coverPlace?.Photo;
            const coverImg = coverImageM && !isBadCoverUrl(coverImageM) ? Common.coverImageHTML(Common.getImageURL(coverImageM, null, true)) : "";
            html = `${coverImg}${html}`;
          }
        }
      }
      let htmlString = html;
      state.HTMLStack = [];
      state.HTMLStack.push(htmlString);
      state.homeScreen = htmlString;
      state.homeScreenKey += 1;
    },
    clearGuideStack: (state) => {
      state.GuideStack = [];
      return state;
    },
    setShowHTMLReader: (state, action) => {
      //state.ShowHTMLReader = action.payload.value;
      return state;
    },
    setShowMultiselect: (state, action) => {
      state.ShowMultiselect = action.payload.value;
    },
    setMultiselectItems: (state, action) => {
      state.MultiselectItems = action.payload.value;
    },
    setMultiselectSelectedItems: (state, action) => {
      state.MultiselectSelectedItems = action.payload.value;
    },
    setMultiselectCallback: (state, action) => {
      state.MultiselectCallback = action.payload.value;
    },
    setMultiselectMode: (state, action) => {
      state.MultiselectMode = action.payload.value;
    },
    setMultiselectSearchText: (state, action) => {
      state.MultiselectSearchText = action.payload.value;
    },
    setMultiselectTitle: (state, action) => {
      state.MultiselectTitle = action.payload.value;
    },

    ClearGuide: (state, action) => {
      state.places = [];
      state.schedule = [];
      state.events = [];
      window.guide_obj.places.placesList = [];
      window.guide_obj.schedule.events = [];
      window.guide_obj.document = "<!-- Comment -->";

      state.FilterVisited = false;
      state.FilterNotVisited = false;
      state.FilterFavorites = false;
      state.ShowHiddenPlaces = false;
      state.FilterMustSee = false;
      state.FilterBucketList = false;
      state.SchedulesFilter = [];
      state.DatesFilter = [];
      state.PlacesFilter = [];
      state.GroupsFilter = [];
      state.CategoriesFilter = [];
      state.FilterSearched = "all";
      state.GroupsHide = [];
      state.SearchText = "";
      state.DistanceFilter = -1;
      state.showHiddenPlace = [];
      state.PlaceTypeFilter = [];

      state.HTMLStack = [];
      return state;
    },

    ClearAllFilters: (state, action) => {
      state.FilterVisited = false;
      state.FilterNotVisited = false;
      state.FilterFavorites = false;
      state.ShowHiddenPlaces = false;
      state.FilterMustSee = false;
      state.FilterBucketList = false;
      state.SchedulesFilter = [];
      state.DatesFilter = [];
      state.PlacesFilter = [];
      state.GroupsFilter = [];
      state.CategoriesFilter = [];
      state.FilterSearched = "all";
      state.GroupsHide = [];
      state.SearchText = "";
      state.DistanceFilter = -1;
      state.showHiddenPlace = [];
      state.PlaceTypeFilter = [];
      state.CitiesFilter =[];
      return state;
    },
    SetFilterVisited: (state, action) => {
      state.FilterVisited = action.payload.value;
    },
    SetFilterNotVisited: (state, action) => {
      state.FilterNotVisited = action.payload.value;
    },
    SetFilterFavorites: (state, action) => {
      state.FilterFavorites = action.payload.value;
    },
    SetShowHiddenPlaces: (state, action) => {
      state.ShowHiddenPlaces = action.payload.value;
    },
    SetFilterMustSee: (state, action) => {
      state.FilterMustSee = action.payload.value;
    },
    SetFilterBucketList: (state, action) => {
      state.FilterBucketList = action.payload.value;
    },
    SetSchedulesFilter: (state, action) => {
      state.SchedulesFilter = action.payload.value;
    },
    SetDatesFilter: (state, action) => {
      state.DatesFilter = action.payload.value;
    },
    SetPlacesFilter: (state, action) => {
      state.PlacesFilter = action.payload.value;
    },
    SetGroupsFilter: (state, action) => {
      state.GroupsFilter = action.payload.value;
    },
    SetCategoriesFilter: (state, action) => {
      state.CategoriesFilter = action.payload.value;
    },
    SetFilterSearched: (state, action) => {
      state.FilterSearched = action.payload.value;
    },
    SetGroupsHide: (state, action) => {
      state.GroupsHide = action.payload.value;
    },
    SetSearchText: (state, action) => {
      state.SearchText = action.payload.value;
    },
    SetGuideSearchText: (state, action) => {
      state.GuideSearchText = action.payload.value;
    },
    setEventSearchText: (state, action) => {
      state.EventSearchText = action.payload.value;
    },
    SetDistanceFilter: (state, action) => {
      state.DistanceFilter = action.payload.value;
    },
    SetCitiesFilter: (state, action) => {
      state.CitiesFilter = action.payload.value;
    },
    SetLoadingGuide: (state, action) => {
      // console.log("SetLoadingGuide", 
      //   state.noFiltering,
      //   state.FilterFavorites,
      //   state.FilterSearched,
      //   state.FilterMustSee,
      //   state.ShowHiddenPlaces,
      //   state.FilterVisited,
      //   state.FilterNotVisited,
      //   state.SchedulesFilter.length,
      //   state.DatesFilter.length,
      //   state.PlacesFilter.length,
      //   state.GroupsFilter.length,
      //   state.GroupsHide.length,
      //   state.CategoriesFilter.length,
      //   state.DistanceFilter,
      //   state.PlaceTypeFilter.length,
      //  ":", state.SearchText, ":");
      state.LoadingGuide = action.payload.value;
    },
    SetSortBy: (state, action) => {
      state.SortBy = action.payload.value;
    },
    setScheduleName: (state, action) => {
      state.ScheduleName = action.payload.value;
    },
    setSelectedEvent: (state, action) => {
      state.SelectedEvent = action.payload.value;
    },
    setUserInfo: (state, action) => {
      state.user_info = action.payload.value;
      //const _Authorization = window.guide_obj.setAuthorization(state.guide_meta.guide_info, action.payload.value.uid);
      //state.Authorization = _Authorization;
    },
    setOwnerInfo: (state, action) => {
      state.owner_info = action.payload.value;
    },
    setCreatorInfo: (state, action) => {
      state.creator_info = action.payload.value;
    },
    setAppSettings: (state, action) => {
      state.AppSettings = action.payload.value;
    },
    setToLoc: (state, action) => {
      state.toLoc = action.payload.value;
    },
    setFromLoc: (state, action) => {
      state.fromLoc = action.payload.value;
    },
    setWaypointsArray: (state, action) => {
      state.waypointsArray = action.payload.value;
    },
    setMenuModalMeta: (state, action) => {
      state.MenuModalMeta = action.payload.value;
    },
    setGuideInfo: (state, action) => {
      state.guide_info = action.payload.value;
    },
    setAuthorization: (state, action) => {
      if (window.guide_obj == null) return state;
      const _Authorization = window.guide_obj.setAuthorization(action.payload.guide_info, action.payload.uid);
      state.Authorization = _Authorization;
      //console.log("setAuthorization", _Authorization, action.payload);
    },
    setGuideSortOption: (state, action) => {
      state.GuideSortOption = action.payload.value;
    },
    addShowHiddenPlaces: (state, action) => {
      // Appends against the reducer's own current state instead of a
      // caller-supplied full list, so callers that fire several dispatches
      // in the same tick (e.g. bulk place import looping add_place_to_map)
      // don't clobber each other with stale closure snapshots of
      // showHiddenPlace — each dispatch is guaranteed to build on the
      // previous one's result. Accepts a single place or an array.
      const additions = Array.isArray(action.payload.value) ? action.payload.value : [action.payload.value];
      state.showHiddenPlace = [...state.showHiddenPlace, ...additions];
    },
    setShowHiddenPlace: (state, action) => {
      console.log("*******setShowHiddenPlace", action.payload.value);
      state.showHiddenPlace = action.payload.value;

      // const places = window.guide_obj.getPlacesToDisplay(
      //   state.SortBy,
      //   state.SearchText,
      //   state.FilterVisited,
      //   state.FilterFavorites,
      //   state.DatesFilter,
      //   state.PlacesFilter,
      //   state.CurrentLocation,
      //   state.GroupsFilter,
      //   state.CategoriesFilter,
      //   state.Section,
      //   state.SearchResultsPlacesList,
      //   state.FilterSearched,
      //   state.ShowHiddenPlaces,
      //   state.ScheduleName,
      //   state.FilterNotVisited,
      //   state.FilterMustSee,
      //   state.SchedulesFilter,
      //   state.GroupsHide,
      //   state.DistanceFilter,
      //   state.user_info,
      //   false,
      //   action.payload.value
      // );

      // state.places = places;
      //console.log(places);

      if (window.guide_obj == null) return state;
      const res = window.guide_obj.preFilterPlaces(
        state.SearchText,
        state.FilterVisited,
        state.FilterFavorites,
        state.DatesFilter,
        state.PlacesFilter,
        state.CurrentLocation,
        state.GroupsFilter,
        state.CategoriesFilter,
        state.Section,
        state.SearchResultsPlacesList,
        state.FilterSearched,
        state.ShowHiddenPlaces,
        state.ScheduleName,
        state.FilterNotVisited,
        state.FilterMustSee,
        state.SchedulesFilter,
        state.GroupsHide,
        state.DistanceFilter,
        state.user_info,
        action.payload.value,
        state.PlaceTypeFilter,
        state.CitiesFilter,
        state.FilterBucketList
      );
      const _MarkersToDisplay = res.filteredPlacesList;
      const _polylines = res.polylines;
      _polylines.forEach((polyline) => {
        polyline.path = polyline.coordinates.map((ll) => ({ lat: ll.latitude, lng: ll.longitude }));
      });
      state.markers = _MarkersToDisplay;
      state.polylines = _polylines;
    },
    // setHTMLContent: (state, action) => {
    //   state.HTMLContent = action.payload.value;
    // },

    selectOne: (state, action) => {
      let { id, checkedIndex, mainIndex } = action.payload;

      // //console.log("action", state[index]);
      state[mainIndex].places[id].detail[checkedIndex].checked =
        !state[mainIndex].places[id].detail[checkedIndex].checked;

      // state[id].detail[index].checked = !state[id].detail[index].checked;
      return state;
    },
    selectAll: (state, action) => {
      if (action.payload.show) {
        state[action.payload.id].detail.forEach((element) => {
          element.checked = true;
        });
      } else {
        state[action.payload.id].detail.forEach((element) => {
          element.checked = false;
        });
      }
    },
  },
});

export const { selectOne, selectAll } = slice1.actions;

export const slice1Actions = slice1.actions;

export default slice1.reducer;
